import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { gql } from "graphql-tag";
import { faDroplet } from "@fortawesome/free-solid-svg-icons";
import { faTemperature2 } from "@fortawesome/free-solid-svg-icons/faTemperature2";
import { faCalendar } from "@fortawesome/free-solid-svg-icons/faCalendar";
import { DataService } from "../../../../services/data.service";
import { GraphqlService } from "../../../../services/graphql.service";
import { NotificationService } from "../../../../services/notification.service";
import { AppState } from "../../../../store";
import { Observable } from "rxjs";
import { TimeRangesEnum } from "../../../../common/interfaces/global-interfaces";
import { Warehouse } from "../../../../store/entities/warehouse-management/warehouse/warehouse.model";
import { StoreItemActions } from "../../../../store/entities/store-item/store-item.actions";
import { StoreItemEntity } from "../../../../store/entities/store-item/store-item.model";
import { selectStoreItems } from "../../../../store/entities/store-item/store-item.selectors";
import { GET_ALL_MSD_WAREHOUSE } from "../../../../store/entities/warehouse-management/warehouse/warehouse.graphql";
import { Device } from "../../../../store/entities/warehouse-management/device/device.model";
import { GET_ALL_SENSORS } from "../../../../store/entities/warehouse-management/device/device.graphql";

interface EnvironmentReport {
  daily_avg_readings: {
    reading_date: string;
    zone_id: string;
    zone_name: string;
    avg_temp: string;
    avg_humidity: string;
  }[];
  extreme_readings: {
    highest_temp: {
      temperature: string;
      sensor_id: string;
      zone_id: string;
      zone_name: string;
      warehouse_id: string;
      warehouse_name: string;
      reading_date: string;
    };
    lowest_temp: {
      temperature: string;
      sensor_id: string;
      zone_id: string;
      zone_name: string;
      warehouse_id: string;
      warehouse_name: string;
      reading_date: string;
    };
    highest_humidity: {
      humidity: string;
      sensor_id: string;
      zone_id: string;
      zone_name: string;
      warehouse_id: string;
      warehouse_name: string;
      reading_date: string;
    };
    lowest_humidity: {
      humidity: string;
      sensor_id: string;
      zone_id: string;
      zone_name: string;
      warehouse_id: string;
      warehouse_name: string;
      reading_date: string;
    };
  };
}

const GET_ENVIRONMENT_SUMMARY_REPORT = gql`
  query GetWarehouseEnvironmentalReport(
    $filtering: ReportsFilteringInputObject
  ) {
    getWarehouseEnvironmentalReport(filtering: $filtering) {
      response {
        id
        status
        code
        message
      }
      data {
        dashboardData
      }
    }
  }
`;
@Component({
  selector: "app-environment-summary-report",
  templateUrl: "./environment-summary-report.component.html",
  styleUrl: "./environment-summary-report.component.scss",
})
export class EnvironmentSummaryReportComponent implements OnInit {
  private store: Store<AppState> = inject(Store<AppState>);
  private ds: DataService = inject(DataService);
  private apollo: GraphqlService = inject(GraphqlService);
  private notificationService: NotificationService =
    inject(NotificationService);

  timeframeTabs: { name: string }[] = [
    { name: "Today" },
    { name: "Yesterday" },
  ];
  @ViewChild("reportContent", { static: false }) reportContent!: ElementRef;
  activeTimeframeTab: string;
  warehouseSensors$: Observable<Device[]>;

  frequencyTabs: { name: string }[] = [
    { name: "Hourly" },
    { name: "Daily" },
    { name: "Weekly" },
    { name: "Monthly" },
  ];
  activeFrequencyTab: string = "Hourly";
  endDate: string;
  startDate: string;
  highestHumiditySummary: any;
  lowestHumiditySummary: any;
  lowestTemperatureSummary: any;
  highestTemperatureSummary: any;
  warehouses$: Observable<Warehouse[]>;
  filtering: {
    warehouse: string;
    sensor: string;
    timeRange: TimeRangesEnum;
    dateTo: string;
    dateFrom: string;
  } = {
    warehouse: null,
    sensor: null,
    timeRange: TimeRangesEnum.Hourly,
    dateFrom: this.ds.formatDate(new Date()) + "T00:00:00",
    dateTo: this.ds.formatDate(new Date()) + "T23:59:59",
  };
  dataset: any = [];
  temperatureData: any = [
    {
      name: "Temperature",
      data: this.dataset,
      color: "#FD9034",
    },
  ];
  temperatureLabels = [];

  humidityDataset = [];
  humidityData: any = [
    {
      name: "Humidity",
      data: this.humidityDataset,
      color: "#151C51",
    },
  ];
  humidityLabels = [];
  showChart: boolean = false;
  noDataMessage: string = null;

  ngOnInit() {
    this.activeTimeframeTab = "Today";

    this.store.dispatch(
      StoreItemActions.getStoreItems({
        entity: StoreItemEntity.warehouse,
        query: GET_ALL_MSD_WAREHOUSE,
        filtering: {},
      }),
    );

    this.warehouses$ = this.store.select(
      selectStoreItems(StoreItemEntity.warehouse),
    );

    // Set default warehouse when list is loaded
    this.warehouses$.subscribe((warehouses) => {
      if (warehouses?.length) {
        this.filtering.warehouse = warehouses[0].uniqueId; // first warehouse as default
        this.generateReport(); // generate report immediately
      }
    });
  }

  onFilterChange(
    evt: string,
    filterType:
      | "timeframe"
      | "timerange"
      | "warehouse"
      | "dateFrom"
      | "dateTo"
      | "sensor",
  ) {
    switch (filterType) {
      case "warehouse":
        this.filtering.warehouse = evt;

        this.store.dispatch(
          StoreItemActions.getStoreItems({
            entity: StoreItemEntity.warehouseSensor,
            query: GET_ALL_SENSORS,
            filtering: { warehouse: this.filtering.warehouse },
          }),
        );

        this.warehouseSensors$ = this.store.select(
          selectStoreItems(StoreItemEntity.warehouseSensor),
        );
        this.warehouseSensors$.subscribe((sensors) => {
          if (sensors?.length) {
            this.filtering.sensor = sensors[0].uniqueId;
          }
        });
        break;

      case "sensor":
        this.filtering.sensor = evt;
        break;
      case "timeframe":
        this.startDate = null;
        this.endDate = null;
        switch (evt) {
          case "Today":
            this.filtering.dateFrom =
              this.ds.formatDate(new Date()) + "T00:00:00";
            this.filtering.dateTo =
              this.ds.formatDate(new Date()) + "T23:59:59";
            break;
          case "Yesterday":
            let yesterdayDate = new Date();
            yesterdayDate.setDate(yesterdayDate.getDate() - 1);
            this.filtering.dateFrom =
              this.ds.formatDate(yesterdayDate) + "T00:00:00";
            this.filtering.dateTo =
              this.ds.formatDate(yesterdayDate) + "T23:59:59";
            break;
        }
        break;
      case "timerange":
        this.activeFrequencyTab = evt;
        switch (evt) {
          case "Hourly":
            this.filtering.timeRange = TimeRangesEnum.Hourly;
            break;
          case "Daily":
            this.filtering.timeRange = TimeRangesEnum.Daily;
            break;
          case "Weekly":
            this.filtering.timeRange = TimeRangesEnum.Weekly;
            break;
          case "Monthly":
            this.filtering.timeRange = TimeRangesEnum.Monthly;
            break;
        }
        this.generateReport();
        break;
      case "dateFrom":
        this.filtering.dateFrom =
          this.ds.formatDate(this.startDate) + "T00:00:00";
        break;
      case "dateTo":
        this.filtering.dateTo = this.ds.formatDate(this.endDate) + "T23:59:59";
        break;
    }
  }

  async generateReport() {
    try {
      const result: any = await this.apollo.query({
        query: GET_ENVIRONMENT_SUMMARY_REPORT,
        variables: { filtering: this.filtering },
      });
      const { response, data } = result?.data?.getWarehouseEnvironmentalReport;
      if (response?.status && data?.dashboardData) {
        this.noDataMessage = null;
        const parsedData: EnvironmentReport = JSON.parse(data.dashboardData);
        this.highestTemperatureSummary = parsedData.extreme_readings.highest_temp;
        this.lowestTemperatureSummary = parsedData.extreme_readings.lowest_temp;
        this.highestHumiditySummary = parsedData.extreme_readings.highest_humidity;
        this.lowestHumiditySummary = parsedData.extreme_readings.lowest_humidity;
        this.temperatureData[0].data = parsedData.daily_avg_readings.map(
          (reading) => reading.avg_temp,
        );
        this.humidityData[0].data = parsedData.daily_avg_readings.map(
          (reading) => reading.avg_humidity,
        );
        this.humidityLabels = parsedData.daily_avg_readings.map(
          (reading) => reading.reading_date,
        );
        this.temperatureLabels = parsedData.daily_avg_readings.map(
          (reading) => reading.reading_date,
        );
        this.showChart = true;
      } else {
        this.showChart = false;
        this.noDataMessage = response?.message || 'No data available.';
      }
    } catch (e) {
      this.showChart = false;
      this.noDataMessage = e.message || 'An error occurred while loading the report.';
    }
  }

  protected readonly faTemperature2 = faTemperature2;
  protected readonly faDroplet = faDroplet;
  protected readonly faCalendar = faCalendar;
}
